import { useState } from "react";
import { BookingStatus } from "@repo/database";
import { VisuallyHidden } from "react-aria";
import { useTranslation } from "react-i18next";

import { cn } from "~/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { RegisterButton } from "../ui/register-button";

interface BookingStatusSelectProps {
  value?: BookingStatus;
  onChange: (status: BookingStatus) => void;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  disabled?: boolean;
}

export function BookingStatusSelect({
  className,
  value,
  onChange,
  onClick,
}: BookingStatusSelectProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const bookingStatus = [
    {
      value: BookingStatus.active,
      label: t("BookingStatus.planned"),
    },
    {
      value: BookingStatus.checked_in,
      label: t("BookingStatus.checked_in"),
    },
    {
      value: BookingStatus.checked_out,
      label: t("BookingStatus.checked_out"),
    },
    {
      value: BookingStatus.no_show,
      label: t("BookingStatus.no_show"),
    },
    {
      value: BookingStatus.canceled,
      label: t("BookingStatus.canceled"),
    },
  ];

  const getStatusColor = (status?: BookingStatus) => {
    switch (status) {
      case BookingStatus.active:
        return "#2563eb";
      case BookingStatus.checked_out:
        return "#a855f7";
      case BookingStatus.checked_in:
        return "#42a978";
      case BookingStatus.no_show:
        return "#dc2626";
      case BookingStatus.canceled:
        return "#808080";
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <RegisterButton
          className={cn("min-w-[100px]", className)}
          onClick={(e) => {
            onClick(e);
            setOpen(true);
          }}
          size="sm"
          color={getStatusColor(value)}
        >
          {bookingStatus.find((status) => status.value === value)?.label}
        </RegisterButton>
      </DialogTrigger>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t("Booking.BookingStatus.title")}</DialogTitle>
          <VisuallyHidden>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <div className="flex flex-col gap-2 p-4">
          {bookingStatus.map((status) => (
            <RegisterButton
              disabled={status.value === value}
              key={status.value}
              onClick={(e) => {
                e.stopPropagation();
                onChange(status.value);
                setOpen(false);
              }}
              className="w-full text-xl transition-colors"
              size="dashboard"
              color={getStatusColor(status.value)}
            >
              {status.label}
            </RegisterButton>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
